/**
 * Internal app role constants and utilities
 * Built on top of the shared permission system.
 */

import {
	canAccessInternalEvents,
	canAccessRoasting,
	canAccessMarketingEmails,
	canManageInternalEvents,
	canPermanentlyDeleteAccountingBills,
	canViewAccountingBills,
	hasAnyPermission,
	hasPermission,
	isInternalRole,
	normalizeRole,
	type Permission,
	type Role,
} from "../../../../packages/backend/convex/helpers/permissions";

export { isCreditKeyPurchaserRole } from "../../../../packages/backend/convex/helpers/creditKeyPurchaserRoles";

export type { Permission, Role };
export {
	canAccessInternalEvents,
	canAccessRoasting,
	canAccessMarketingEmails,
	canManageInternalEvents,
	canPermanentlyDeleteAccountingBills,
	canViewAccountingBills,
	hasPermission,
	isInternalRole as isInternalUser,
};

export const INTERNAL_ROLES = {
	ADMIN: "admin",
	SALES: "sales",
	LOGISTICS: "logistics",
	WAREHOUSE: "warehouse",
	ACCOUNTING: "accounting",
	INVENTORY: "inventory",
} as const;

export type InternalRole = (typeof INTERNAL_ROLES)[keyof typeof INTERNAL_ROLES];

type RoleValue = string | null | undefined;

export function isAdmin(role: RoleValue): boolean {
	return normalizeRole(role) === "admin";
}

export function normalizeInternalRole(role: RoleValue): InternalRole | null {
	const r = normalizeRole(role);
	if (!r) return null;
	return r as InternalRole;
}

export function hasRole(
	userRole: RoleValue,
	requiredRole: InternalRole,
): boolean {
	return normalizeRole(userRole) === requiredRole;
}

/**
 * Path access uses hasAnyPermission to allow cross-domain browsing
 * (e.g. inventory can browse /logistics pages, logistics can browse /inventory).
 * This is separate from nav visibility which uses a single permission.
 */
const PATH_ACCESS_PERMISSIONS: Record<string, Permission[]> = {
	"/admin/analytics": ["admin:view"],
	"/admin/leads": ["admin:view"],
	"/admin/copilot": ["admin:view"],
	"/admin/discounts": ["admin:view"],
	"/admin/events": ["admin:view"],
	"/sales/discounts": ["admin:view"],
	"/sales/discount": ["admin:view"],
	"/support/agents": ["admin:view"],
	"/cargo-label": ["inventory:view"],
	"/inventory/samples": ["samples:view"],
	"/inventory": ["inventory:view"],
	"/sales/contracts/new": ["contracts:approve"],
	"/sales/contracts": ["contracts:view"],
	"/sales/crm/leads": ["sales:view"],
	"/sales/crm/follow-ups": ["sales:view"],
	"/sales/crm/people": ["sales:view"],
	"/sales/crm/opportunities": ["sales:view"],
	"/sales/crm": ["sales:view"],
	"/sales/samples": ["samples:view"],
	"/sales/leads": ["sales:view"],
	"/sales/follow-ups": ["sales:view"],
	"/sales/emails": ["sales:view"],
	"/sales/users": ["sales:view"],
	"/sales": ["sales:view"],
	"/sales/analytics": ["sales:view"],
	"/logistics/inbound-deliveries": ["logistics:edit"],
	"/logistics/international-purchases": ["logistics:edit", "accounting:view"],
	"/logistics/outbound-shipments": ["logistics:edit"],
	"/logistics/shipping-adjustments": ["logistics:edit"],
	"/logistics/shipping-quotes": ["logistics:edit"],
	"/logistics/carrier-notices": ["logistics:edit"],
	"/logistics/users": ["logistics:edit", "inventory:edit"],
	"/logistics": ["logistics:view", "inventory:view"],
	"/support": ["internal:access"],
	"/invoice": ["accounting:view", "logistics:edit"],
	"/admin/accounting": ["accounting:view"],
	"/security": ["internal:access"],
	"/admin": ["admin:view"],
	"/docs": ["internal:access"],
	"/changelog": ["internal:access"],
};

const INTERNAL_ROLE_DEFAULT_PATHS: Record<InternalRole, string> = {
	[INTERNAL_ROLES.ADMIN]: "/admin",
	[INTERNAL_ROLES.SALES]: "/sales",
	[INTERNAL_ROLES.LOGISTICS]: "/logistics",
	[INTERNAL_ROLES.WAREHOUSE]: "/logistics",
	[INTERNAL_ROLES.ACCOUNTING]: "/admin/accounting",
	[INTERNAL_ROLES.INVENTORY]: "/inventory",
};

export function getDefaultInternalPathForRole(role: RoleValue): string {
	const normalizedRole = normalizeInternalRole(role);
	if (!normalizedRole) {
		return "/admin";
	}
	return INTERNAL_ROLE_DEFAULT_PATHS[normalizedRole];
}

export function canAccessInternalPath(
	role: RoleValue,
	pathname: string,
	email?: string | null,
): boolean {
	if (!isInternalRole(role)) return false;
	if (pathname === "/roasting" || pathname.startsWith("/roasting/")) {
		return canAccessRoasting(role, email);
	}

	if (pathname.startsWith("/admin/accounting/bills")) {
		const relativePath = pathname.slice("/admin/accounting/bills".length);
		const isBillsReviewPath =
			relativePath === "" ||
			relativePath === "/" ||
			relativePath === "/aci" ||
			relativePath === "/aci/" ||
			(/^\/[^/]+\/?$/.test(relativePath) &&
				relativePath !== "/settings" &&
				relativePath !== "/settings/");
		if (isBillsReviewPath) return canViewAccountingBills(role, email);
	}

	if (
		pathname.startsWith("/marketing/events") ||
		pathname.startsWith("/admin/events")
	) {
		return canAccessInternalEvents(role, email);
	}

	if (
		pathname.startsWith("/marketing/users") ||
		pathname.startsWith("/sales/users") ||
		pathname.startsWith("/sales/crm/people")
	) {
		return hasPermission(role, "sales:view") || canAccessMarketingEmails(role, email);
	}

	if (
		pathname.startsWith("/marketing/campaigns") ||
		pathname.startsWith("/marketing/emails") ||
		pathname.startsWith("/sales/emails")
	) {
		return canAccessMarketingEmails(role, email);
	}

	if (pathname.startsWith("/marketing")) {
		return canAccessInternalEvents(role, email) || canAccessMarketingEmails(role, email);
	}

	// Check from most specific to least specific path prefix
	for (const [prefix, permissions] of Object.entries(
		PATH_ACCESS_PERMISSIONS,
	).sort((a, b) => b[0].length - a[0].length)) {
		if (pathname.startsWith(prefix)) {
			return hasAnyPermission(role, permissions);
		}
	}

	return false;
}
